import type { Entry } from '../types';
import { escapeHtml } from './util';

/** Static row-shaped placeholders for initial load — no shimmer, no motion. */
export function renderSkeleton(grid: HTMLElement, count = 10): void {
  grid.classList.remove('list--noanalytics');
  grid.innerHTML = Array.from({ length: count })
    .map(
      () => `
      <div class="row row-skeleton" aria-hidden="true">
        <span class="skel skel-name"></span>
        <span class="skel skel-desc"></span>
      </div>`,
    )
    .join('');
}

const SOURCE_LABELS: Record<string, string> = {
  tools: 'tools index',
  sites: 'sites index',
};

/** Hairline notice line when one index failed but the other loaded. */
export function renderBanner(container: HTMLElement, failures: string[], fromCache: boolean, onRetry: () => void): void {
  if (failures.length === 0 && !fromCache) {
    container.innerHTML = '';
    return;
  }
  const parts: string[] = [];
  if (failures.length) {
    const what = failures.map((f) => SOURCE_LABELS[f] ?? f).join(' and ');
    parts.push(`Couldn’t reach the ${what}${fromCache ? '' : ' — showing what loaded'}.`);
  }
  if (fromCache) parts.push('Showing a cached list.');
  container.innerHTML = `
    <div class="notice" role="status">
      <span class="notice-mark${fromCache && !failures.length ? ' notice-mark-quiet' : ''}">■</span>
      <span class="notice-text">${escapeHtml(parts.join(' '))}</span>
      <button class="text-link notice-retry" data-action="retry">Retry</button>
      <button class="text-link text-link-quiet" data-action="dismiss" aria-label="Dismiss">×</button>
    </div>`;
  container.querySelector('[data-action="retry"]')?.addEventListener('click', onRetry);
  container.querySelector('[data-action="dismiss"]')?.addEventListener('click', () => {
    container.innerHTML = '';
  });
}

/** Full state when nothing could be loaded at all. */
export function renderFatal(grid: HTMLElement, onRetry: () => void): void {
  grid.innerHTML = `
    <div class="bigstate">
      <p class="bigstate-title">Can’t reach the indexes.</p>
      <p class="bigstate-sub">Both factory indexes failed to load and there’s no cached copy yet.</p>
      <button class="text-link notice-retry" data-action="retry">Retry</button>
    </div>`;
  grid.querySelector('[data-action="retry"]')?.addEventListener('click', onRetry);
}

/** No-results state: typographic, left-aligned, with a few discoveries. */
export function renderEmpty(
  grid: HTMLElement,
  query: string,
  suggestions: Entry[],
  onClear: () => void,
  onSuggestion: (slug: string) => void,
): void {
  const tries = suggestions
    .map(
      (s) =>
        `<button class="text-link suggestion" data-slug="${escapeHtml(s.slug)}">${escapeHtml(s.name)}</button>`,
    )
    .join('<span class="fi-count"> · </span>');
  grid.innerHTML = `
    <div class="bigstate">
      <p class="bigstate-title">Nothing matches${query ? ` “${escapeHtml(query)}”` : ''}.</p>
      <p><button class="text-link bigstate-clear" data-action="clear">Clear filters</button></p>
      ${tries ? `<p class="bigstate-sub">Try: ${tries}</p>` : ''}
    </div>`;
  grid.querySelector('[data-action="clear"]')?.addEventListener('click', onClear);
  grid.querySelectorAll<HTMLButtonElement>('.suggestion').forEach((btn) => {
    btn.addEventListener('click', () => onSuggestion(btn.dataset.slug ?? ''));
  });
}
